import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  Cpu,
  FileText,
  Mic,
  Shield,
  Upload,
  Workflow,
} from 'lucide-react';
import { useRevealOnScroll } from '../../../hooks/useRevealOnScroll';
import {
  FEATURE_DIKTAT,
  PRODUCT_NAME_MARKETING,
} from '../../../constants';
import { T05ProductShell } from './T05ProductShell';
import { T05ReferoFeatureRow } from './T05ReferoFeatureRow';
import { T05HeroLeadForm } from '../../landing-templates/t05-launchdarkly/T05HeroLeadForm';
import { T05LeadModal } from '../../landing-templates/t05-launchdarkly/T05LeadModal';
import { useT05ProductLead } from '../../../lib/t05ProductLead';
import type { T05ProductPageProps } from './routes.fragment';
import './t05-product.css';

const DIKTAT_LEAD = { interest: 'Orbis Diktat – Demo', quelle: 't05-product-diktat' };

const DIKTAT_NAV = [
  { href: '#pipeline', label: 'Pipeline' },
  { href: '#upload', label: 'Upload' },
  { href: '#lokal', label: 'Lokal & GPU' },
];

const PIPELINE_STEPS = [
  { label: 'Audio-Upload', badge: 'Portal/Matrix' },
  { label: 'Whisper large-v3', badge: 'Lokal' },
  { label: 'Mistral via Ollama', badge: 'Analyse' },
  { label: 'LEXCORE/BRAIN', badge: 'Kontext' },
  { label: 'Gotenberg PDF', badge: 'Vermerk' },
  { label: 'Nextcloud-Ablage', badge: 'Verschlüsselt' },
];

interface ProductDiktatProps extends T05ProductPageProps {}

function DiktatPipelineScreen() {
  return (
    <div className="t05-product-panel" aria-label="Diktat-Pipeline">
      <div className="t05-product-panel__bar" aria-hidden>
        <span className="t05-product-panel__dot" />
        <span className="t05-product-panel__dot" />
        <span className="t05-product-panel__dot" />
      </div>
      <p className="t05-product-panel__title">diktat://pipeline</p>
      <div className="t05-product-panel__grid">
        {PIPELINE_STEPS.map((step) => (
          <div key={step.label} className="t05-product-panel__row">
            <span>{step.label}</span>
            <span className="t05-product-panel__badge">{step.badge}</span>
            <span className="t05-product-panel__status" aria-hidden />
          </div>
        ))}
      </div>
    </div>
  );
}

function DiktatUploadScreen() {
  return (
    <div className="t05-prod-screen" aria-label="Diktat-Upload — Platzhalter">
      <div className="t05-prod-screen__chrome">
        <div className="t05-prod-screen__dots" aria-hidden>
          <span />
          <span />
          <span />
        </div>
        <span className="t05-prod-screen__url">portal://diktat/upload</span>
      </div>
      <div className="t05-prod-screen__body">
        <div className="t05-prod-screen__chat-bubble t05-prod-screen__chat-bubble--user">
          mandat_weber_diktat_03-2026.m4a · 4,2 MB
        </div>
        <div className="t05-prod-screen__row">
          <span>Whisper large-v3 · GPU aktiv</span>
          <span className="t05-prod-screen__badge">Lokal</span>
        </div>
        <div className="t05-prod-screen__row">
          <span>Aktenvermerk_Weber.pdf</span>
          <span className="t05-product-panel__badge">Fertig</span>
        </div>
      </div>
    </div>
  );
}

function EdgeGpuPanel() {
  return (
    <div className="t05-product-panel" aria-label="Edge-GPU — Platzhalter">
      <div className="t05-product-panel__bar" aria-hidden>
        <span className="t05-product-panel__dot" />
        <span className="t05-product-panel__dot" />
        <span className="t05-product-panel__dot" />
      </div>
      <p className="t05-product-panel__title">edge://gpu/whisper</p>
      <div className="t05-product-panel__grid">
        {[
          { label: 'Whisper large-v3', badge: 'GPU' },
          { label: 'Ollama Mistral', badge: 'Lokal' },
          { label: 'Gotenberg PDF', badge: 'Edge' },
          { label: 'Datenexport', badge: '0 %' },
        ].map((row) => (
          <div key={row.label} className="t05-product-panel__row">
            <span>{row.label}</span>
            <span className="t05-product-panel__badge">{row.badge}</span>
            <span className="t05-product-panel__status" aria-hidden />
          </div>
        ))}
      </div>
    </div>
  );
}

export const ProductDiktat: React.FC<ProductDiktatProps> = ({ onLoginClick, onLeadSubmit }) => {
  useRevealOnScroll();

  const lead = useT05ProductLead({ onLeadSubmit }, DIKTAT_LEAD);
  const openDemo = () => lead.openDemo(DIKTAT_LEAD.interest);

  return (
    <T05ProductShell
      productLabel={FEATURE_DIKTAT}
      navAnchors={DIKTAT_NAV}
      onLoginClick={onLoginClick}
      onDemoClick={openDemo}
    >
      <header className="t05-hero t05-hero--product t05-hero--compact">
        <p className="t05-eyebrow reveal-up">Sprache → Aktenvermerk · Edge · EU</p>
        <h1 className="t05-headline t05-headline--product reveal-up">
          Diktat.
          <span className="t05-headline__accent">Auf Ihrem Server.</span>
        </h1>
        <p className="t05-lead reveal-up">
          Sprachnachricht hochladen, lokal transkribieren und als fertiges Aktenvermerk-PDF ablegen —
          <strong> Whisper, Ollama und Gotenberg</strong> laufen auf Ihrem Edge-Server. Kein US-Cloud-Upload.
        </p>
        <T05HeroLeadForm lead={lead} />
      </header>

      <section className="t05-trust-strip t05-container reveal-up" aria-label="Vertrauensmerkmale">
        <div className="t05-trust-strip__row">
          {['Whisper large-v3', 'GPU lokal', '0 % Datenexport', 'In Tarifen enthalten'].map((fact, i) => (
            <React.Fragment key={fact}>
              {i > 0 && <span className="t05-trust-strip__sep" aria-hidden>·</span>}
              <span className="t05-trust-strip__item">{fact}</span>
            </React.Fragment>
          ))}
        </div>
      </section>

      <section className="t05-container t05-refero-features" aria-label="Diktat Funktionen">
        <T05ReferoFeatureRow
          id="pipeline"
          accent="voltage"
          label="Pipeline"
          labelIcon={Workflow}
          title="Von der Sprachnachricht zum PDF-Vermerk"
          description="n8n orchestriert den Ablauf auf Ihrem Server — jeder Schritt nachvollziehbar, temporäre Dateien werden gelöscht."
          bullets={[
            { icon: Mic, text: 'Transkription mit Whisper large-v3 auf Ihrer GPU' },
            { icon: FileText, text: 'Strukturierter Aktenvermerk als PDF via Gotenberg' },
            { icon: Check, text: 'Ablage in Nextcloud — mandatsbezogen, verschlüsselt' },
          ]}
          visual={<DiktatPipelineScreen />}
        />

        <T05ReferoFeatureRow
          id="upload"
          reverse
          accent="coral"
          label="Upload-Kanäle"
          labelIcon={Upload}
          title="Portal oder Matrix — Ihre Wahl"
          description="Diktate direkt im Kanzlei-Portal hochladen oder per Matrix-Messenger senden — beides Ende-zu-Ende verschlüsselt auf Ihrem Server."
          bullets={[
            { icon: Upload, text: 'Portal: Drag & Drop im Diktat-Reiter' },
            { icon: Shield, text: 'Matrix: E2EE-Upload aus dem Team-Chat' },
            { icon: Check, text: 'Ergebnis erscheint in Dokumente und Nextcloud' },
          ]}
          footer={
            <Link to="/produkt/portal" className="t05-stack-item__cta">
              Kanzlei-Portal ansehen <ArrowRight size={16} aria-hidden />
            </Link>
          }
          visual={<DiktatUploadScreen />}
        />

        <T05ReferoFeatureRow
          id="lokal"
          accent="trust"
          label="Edge-Infrastruktur"
          labelIcon={Cpu}
          title="Lokal auf Ihrer GPU — kein Fremd-API"
          description="Whisper und Ollama laufen auf Ihrem dedizierten Hetzner-Server. Audiodaten verlassen Ihre Instanz nicht — weder für Transkription noch für die Textanalyse."
          bullets={[
            { icon: Cpu, text: 'GPU-beschleunigte Transkription (Whisper large-v3)' },
            { icon: Shield, text: 'Temporäre Dateien werden nach Verarbeitung gelöscht' },
            { icon: Check, text: `Baustein von ${PRODUCT_NAME_MARKETING} — kein Zusatz-Abo` },
          ]}
          visual={<EdgeGpuPanel />}
        />
      </section>

      <section className="t05-prod-section--tight t05-container reveal-up">
        <div className="t05-prod-disclaimer">
          <strong>Hinweis:</strong> {FEATURE_DIKTAT} erstellt Entwürfe und Vermerke — die anwaltliche
          Prüfung und Freigabe bleiben bei Ihnen.
        </div>
      </section>

      <section className="t05-final-cta t05-container reveal-up">
        <div className="t05-final-cta__band">
          <div className="t05-final-cta__inner">
            <p className="t05-kicker">Teil von Orbis</p>
            <h2 className="t05-title">{FEATURE_DIKTAT} in Orbis</h2>
            <p className="t05-subtitle">
              Teil der All-in-One-Plattform — Self-Hosted, ein Login,
              volle Kontrolle über Ihre Audiodaten.
            </p>
            <div className="t05-cta-actions">
              <button type="button" className="t05-btn t05-btn--primary" onClick={openDemo}>
                Demo anfragen
              </button>
              <Link to="/produkt/orbis" className="t05-btn t05-btn--ghost">
                Orbis ansehen
              </Link>
            </div>
          </div>
        </div>
      </section>
      <T05LeadModal lead={lead} />
    </T05ProductShell>
  );
};
